import {
  KeyValue,
  Unboxed,
  SetValueAction,
  box,
  SetUserDefinedPropertyAction,
  FormControlState,
  FormControlValueTypes,
  FormGroupState,
  unbox
} from 'ngrx-forms';
import {
  OnDestroy,
  OnInit,
  Injector
} from '@angular/core';
import {
  Observable,
  Subscription,
  EMPTY,
  Subject
} from 'rxjs';
import {
  Store,
  MemoizedSelector
} from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import {
  map,
  distinctUntilChanged,
  filter,
  tap,
  takeUntil
} from 'rxjs/operators';
import {
  IDxForm,
  FormControlLike
} from './form';
import { DxFormGroup } from './form-group';
import { deepEqual } from '@digitaix/helpers';
import { ON_EVENT_USER_DEFINED_PROPERTY } from './decorators/controls/user-defined-property-keys';
import { OnEventMapEntry } from './decorators/controls/on-event';
import { v1 as uuid } from 'uuid';
import { StepControl } from './step-control';
import { DxSetValueAction } from './forms.actions';

export const FORM_CONTROL_DISABLED_KEY = 'digitaix-forms-DISABLED';

export const FORM_CONTROL_REQUIRED_KEY = 'digitaix-forms-REQUIRED';

export const FORM_CONTROL_PLACEHOLDER_KEY = 'digitaix-forms-PLACEHOLDER';

export const FORM_CONTROL_LABEL_KEY = 'digitaix-forms-LABEL';

export const FORM_CONTROL_READONLY_KEY = 'digitaix-forms-READONLY';

export const FORM_CONTROL_FX_FLEX_KEY = 'digitaix-forms-FX_FLEX';

export const FORM_CONTROL_FX_HIDE_KEY = 'digitaix-forms-FX_HIDE';

export enum FormControlSelectors {
  OPTIONS = 'options'
}

export interface FormControlSelectorsMap {
  [selectorName: string]: MemoizedSelector<any, any>;
}

export class DxFormControl<
  FormValue extends FormControlValueTypes,
  GroupFormValue extends KeyValue = any,
  RootGroupFormValue extends KeyValue = GroupFormValue
>
  implements
    IDxForm<FormValue, GroupFormValue, RootGroupFormValue>,
    OnDestroy,
    OnInit {
  public disabledFunction: (
    value: Unboxed<GroupFormValue>,
    state: FormGroupState<GroupFormValue>
  ) => boolean;
  public get fxHide(): boolean {
    return this._fxHide;
  }

  public set fxHide(value: boolean) {
    if (this._fxHide !== value) {
      this._fxHide = value;
      if (this._initialized) {
        this.store$.dispatch(
          new SetUserDefinedPropertyAction(
            this.__controlId,
            FORM_CONTROL_FX_HIDE_KEY,
            value
          )
        );
      }
    }
  }
  public get fxFlex(): string {
    return this._fxFlex;
  }

  public get __stepControl(): FormControlLike<FormValue> {
    return new StepControl(this.store$, this.__state$);
  }

  public set fxFlex(value: string) {
    if (this._fxFlex !== value) {
      this._fxFlex = value;
      if (this._initialized) {
        this.store$.dispatch(
          new SetUserDefinedPropertyAction(
            this.__controlId,
            FORM_CONTROL_FX_FLEX_KEY,
            value
          )
        );
      }
    }
  }
  public get __initialized(): boolean {
    return this._initialized;
  }

  /**
   * @internal
   */
  public set __initialized(value: boolean) {
    this._initialized = value;
    if (this._initialized) {
      this.ngAfterInit();
    }
  }

  public get readonly(): boolean {
    return this._readonly;
  }

  public set readonly(value: boolean) {
    if (this._readonly !== value) {
      this._readonly = value;
      if (this._initialized) {
        this.store$.dispatch(
          new SetUserDefinedPropertyAction(
            this.__controlId,
            FORM_CONTROL_READONLY_KEY,
            value
          )
        );
      }
    }
  }

  public get label(): string | null {
    return this._label;
  }

  public set label(value: string | null) {
    if (this._label !== value) {
      if (value === null || typeof value === 'string') {
        this._label = value;
      }
      if (this._initialized) {
        this.store$.dispatch(
          new SetUserDefinedPropertyAction(
            this.__controlId,
            FORM_CONTROL_LABEL_KEY,
            value
          )
        );
      }
    }
  }

  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: boolean) {
    if (this._disabled !== value) {
      this._disabled = value;
      if (this._initialized) {
        this.store$.dispatch(
          new SetUserDefinedPropertyAction(
            this.__controlId,
            FORM_CONTROL_DISABLED_KEY,
            value
          )
        );
      }
    }
  }
  public get required(): boolean {
    return this._required;
  }

  public set required(value: boolean) {
    if (this._required !== value) {
      this._required = value;
      if (this._initialized) {
        this.store$.dispatch(
          new SetUserDefinedPropertyAction(
            this.__controlId,
            FORM_CONTROL_REQUIRED_KEY,
            value
          )
        );
      }
    }
  }
  public get placeholder(): string {
    return this._placeholder;
  }

  public set placeholder(value: string) {
    if (this._placeholder !== value) {
      this._placeholder = value;
      if (this._initialized) {
        this.store$.dispatch(
          new SetUserDefinedPropertyAction(
            this.__controlId,
            FORM_CONTROL_PLACEHOLDER_KEY,
            value
          )
        );
      }
    }
  }
  public __controlId: string;

  public __controlName: string;

  public __value$: Observable<Unboxed<FormValue> | null>;

  public __value: Unboxed<FormValue> | null;

  public __state$: Observable<FormControlState<FormValue>>;

  public __state: FormControlState<FormValue>;

  public __initial: FormValue | null = null;

  public userDefinedProperties: { [key: string]: any } = {};

  public boxed = false;

  public disabledSelector: MemoizedSelector<any, boolean>;

  public fxHideSelector: MemoizedSelector<any, boolean>;

  public selectors: FormControlSelectorsMap = {};

  public internal: boolean = false;

  public __uuid = uuid();

  private _readonly: boolean;

  private _placeholder: string;

  private _label: string | null;

  private _required: boolean;

  private _disabled: boolean;

  private _fxFlex: string;

  private _fxHide: boolean;

  __parent: DxFormGroup<GroupFormValue, any, RootGroupFormValue>;

  __root: DxFormGroup<RootGroupFormValue, null, null>;

  private _subscriptions = new Subscription();

  private _initialized = false;

  public __onInit$ = new Subject<void>();
  public __onDestroy$ = new Subject<void>();
  public __onReset$ = new Subject<void>();

  constructor(
    public readonly store$: Store<any>,
    public readonly logger: NGXLogger,
    public readonly injector: Injector
  ) {
    this.processDecorator();
  }

  public getSelector<T>(selectorName): MemoizedSelector<any, T> | null {
    return this.selectors[selectorName] || null;
  }

  public setControlId(controlId: string, parentId?: string): void {
    this.__controlName = controlId;
    this.__controlId = [parentId, controlId].filter(Boolean).join('.');
  }

  public ngOnDestroy(): void {
    this.logger.trace(
      `[${this.__uuid}::DxFormControl::ngOnDestroy::${this.__controlId}] call`
    );
    this._subscriptions.unsubscribe();
    this._subscriptions = new Subscription();
    this.__value = null;
    this.__value$ = EMPTY;
    this.__onDestroy$.next();
  }

  public init(): void {
    if (
      this._label === undefined ||
      (this._label && this._label.match(/^FORMS\./))
    ) {
      const formId = this.__controlId.match(/^[^\.]+/)[0];
      const controlId = this.__controlId.match(/^[^\.]+\.(.+)/)[1];
      this._label = ['FORMS', formId, 'LABELS', controlId]
        .join('.')
        .replace(/\.[0-9]+\./g, '.');
    }
  }

  public ngOnInit(): void {
    this.logger.trace(
      `[${this.__uuid}::DxFormControl::ngOnInit::${this.__controlId}] call`
    );
    this.__value$ = this.__parent.__value$.pipe(
      filter(Boolean),
      map(value => value[this.__controlName]),
      distinctUntilChanged((a, b) => this.isEqual(a, b)),
      tap(value =>
        this.logger.trace(
          `[${this.__uuid}::DxFormControl::ngOnInit::${
            this.__controlId
          }] new value`,
          value
        )
      ),
      takeUntil(this.__onDestroy$)
    );
    this.__state$ = this.__parent.__state$.pipe(
      filter(Boolean),
      map((state: FormGroupState<any>) => state.controls[this.__controlName]),
      distinctUntilChanged((a: any, b: any) => this.isEqual(a, b)),
      filter(Boolean),
      takeUntil(this.__onDestroy$)
    );
    this._subscriptions.add(
      this.__state$.subscribe(state => (this.__state = state))
    );
    this._subscriptions.add(
      this.__value$.subscribe(value => (this.__value = value))
    );
    if (this.disabledSelector) {
      this._subscriptions.add(
        this.store$
          .select(this.disabledSelector)
          .pipe(distinctUntilChanged())
          .subscribe(disabled => (this.disabled = !!disabled))
      );
    }
    if (this.disabledFunction) {
      this._subscriptions.add(
        this.__parent.__state$
          .pipe(
            map(state => this.disabledFunction(unbox(state.value), state)),
            distinctUntilChanged()
          )
          .subscribe(disabled => (this.disabled = !!disabled))
      );
    }
    if (this.fxHideSelector) {
      this._subscriptions.add(
        this.store$
          .select(this.fxHideSelector)
          .pipe(distinctUntilChanged())
          .subscribe(fxHide => (this.fxHide = !!fxHide))
      );
    }
    if (this._initialized) {
      this.ngAfterInit();
    }
    this.__onInit$.next();
  }

  public ngAfterInit(): void {
    this.store$.dispatch(
      new SetUserDefinedPropertyAction(
        this.__controlId,
        FORM_CONTROL_PLACEHOLDER_KEY,
        this._placeholder
      )
    );
    this.store$.dispatch(
      new SetUserDefinedPropertyAction(
        this.__controlId,
        FORM_CONTROL_LABEL_KEY,
        this._label
      )
    );
    this.store$.dispatch(
      new SetUserDefinedPropertyAction(
        this.__controlId,
        FORM_CONTROL_REQUIRED_KEY,
        this._required
      )
    );
    this.store$.dispatch(
      new SetUserDefinedPropertyAction(
        this.__controlId,
        FORM_CONTROL_DISABLED_KEY,
        this._disabled
      )
    );
    this.store$.dispatch(
      new SetUserDefinedPropertyAction(
        this.__controlId,
        FORM_CONTROL_READONLY_KEY,
        this._readonly
      )
    );
    this.store$.dispatch(
      new SetUserDefinedPropertyAction(
        this.__controlId,
        FORM_CONTROL_FX_FLEX_KEY,
        this._fxFlex
      )
    );
    this.store$.dispatch(
      new SetUserDefinedPropertyAction(
        this.__controlId,
        FORM_CONTROL_FX_HIDE_KEY,
        this._fxHide
      )
    );
    this.processUserDefinedProperties();
  }

  public isEqual(a: Unboxed<FormValue>, b: Unboxed<FormValue>) {
    return deepEqual(a, b);
  }

  public processDecorator(): void {}

  public processUserDefinedProperties(): void {
    if (
      this.userDefinedProperties.hasOwnProperty(ON_EVENT_USER_DEFINED_PROPERTY)
    ) {
      this.userDefinedProperties = {
        ...this.userDefinedProperties,
        [ON_EVENT_USER_DEFINED_PROPERTY]: (<Array<OnEventMapEntry<any, any>>>(
          this.userDefinedProperties[ON_EVENT_USER_DEFINED_PROPERTY]
        )).map(onEvent => ({
          ...onEvent,
          handler: function(this: DxFormControl<any>, $event) {
            const result = onEvent.handler.call(this, $event);
            if (Array.isArray(result)) {
              result.forEach(action => this.store$.dispatch(action));
            } else {
              this.store$.dispatch(result);
            }
          }.bind(this)
        }))
      };
    }
    Object.entries(this.userDefinedProperties).forEach(([key, value]) =>
      this.store$.dispatch(
        new SetUserDefinedPropertyAction(this.__controlId, key, value)
      )
    );
  }

  public setValue(value: Unboxed<FormValue>): void {
    this.logger.trace(
      `[${this.__uuid}::DxFormControl::setValue::${this.__controlId}] call`,
      value
    );
    // TODO : check if already boxed
    this.store$.dispatch(new DxSetValueAction(this.__controlId, this.box(value), `FormControl(${this.constructor.name})::setValue`));
  }

  public box(value: Partial<Unboxed<FormValue>>): FormValue {
    return this.boxed ? box(value) : (value as any);
  }
}
